import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import {
  fundNameGenerator,
  nameGenerator,
  documentNameGenerator,
} from '@literature/literature-utils';
import { TranslateService } from '@shared/translate/translate.service';
import get from 'lodash/get';
import { IUserProfile, UserRole } from '@services/profile.interface';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProfileService } from '@services/profile.service';
import { LiteratureDocumentListingData } from '@types';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { SH } from '@utils/app.constants';
import { StorageService } from '@services';
import { ResponsiveService } from '@frk/eds-components';

@Component({
  selector: 'ft-fund-name-cell-renderer',
  templateUrl: './fund-name-cell-renderer.component.html',
  styleUrls: ['./fund-name-cell-renderer.component.scss'],
})
export class FundNameCellRendererComponent implements OnChanges, OnDestroy {
  @Input() cellRendererData: LiteratureDocumentListingData;
  isSiteIntl: boolean;
  fundName: string;
  fundLink: string;
  clientUse: string;
  loginRequired: boolean;
  isLoggedIn: boolean;
  isUpdateNotificationEnabled: boolean;
  updateNotificationMessage: string;
  showUpdateNotification = false;
  isShareHolderProfile = false;
  showOnlyDocumentName: boolean;
  showOnlyTitle: boolean;
  isColumnLayout: boolean;
  componentName: string;
  isCanada: boolean;
  public profileData: IUserProfile;
  public params: LiteratureDocumentListingData;
  public loginSubscription: Subscription;
  private unsubscribe$: Subject<void> = new Subject<void>();

  isMobile$: Observable<boolean>;
  constructor(
    private translateService: TranslateService,
    private profileService: ProfileService,
    private literatureDataService: LiteratureDataService,
    private storageService: StorageService,
    private responsiveService: ResponsiveService
  ) {
    this.isMobile$ = this.responsiveService.isMobile$();
  }

  ngOnChanges(): void {
    if (this.cellRendererData) {
      this.agInit(this.cellRendererData);
    }
  }

  agInit(params: any) {
    this.params = params.data;
    this.isSiteIntl = get(params, 'isSiteIntl', true);
    this.isCanada = get(params, 'isCanada', false);
    const litDetailUrl = get(params, 'litDetailUrl');
    this.showOnlyDocumentName = get(params, 'showOnlyDocumentName', false);
    this.showOnlyTitle = get(params, 'showOnlyTitle', false);
    this.isColumnLayout = get(params, 'isColumnLayout', false);
    this.componentName = get(params, 'componentName', 'default');
    this.loginRequired = this.params.literatureData.loginRequired;

    this.literatureDataService
      .getProfileData$()
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: IUserProfile) => {
        this.profileData = data;
        this.isLoggedIn = this.profileData?.isLoggedIn;
        this.isShareHolderProfile =
          UserRole[this.profileData?.profileInfo?.role] ===
          SH.toLocaleLowerCase();
      });
    if (this.params.literatureData.clientUse?.trim() !== '') {
      this.clientUse = this.params.literatureData.clientUse;
    }
    if (this.showOnlyDocumentName) {
      this.fundName = documentNameGenerator(
        params.data,
        this.translateService,
        this.showOnlyTitle
      );
      if (!this.isSiteIntl) {
        this.fundLink = `${litDetailUrl}/${params.data?.literatureData?.docId}`;
      }
    } else {
      if (this.isSiteIntl) {
        this.fundName = nameGenerator(
          params.data,
          this.translateService,
          this.isCanada
        );
      } else {
        this.fundName = fundNameGenerator(params.data, this.translateService);
        this.fundLink = `${litDetailUrl}/${params.data?.literatureData?.docId}`;
      }
    }
    this.isUpdateNotificationEnabled = get(
      params,
      'isUpdateNotificationEnabled',
      false
    );
    if (this.isUpdateNotificationEnabled) {
      this.isUpdateMessageRequired();
    }
  }

  storeDefaultPage(event: MouseEvent) {
    event?.preventDefault();
    this.storageService.store<number>('ToSetPage', 1, true);
  }
  /**
   *  to set the update message whenever order date  is older than the publication date
   */
  isUpdateMessageRequired() {
    const newPublicationDateFormat = this.params.literatureData.publicationDate
      ? this.params.literatureData.publicationDate.split('-')
      : [];
    const newOrderDateFormat = this.params.literatureData.orderDate
      ? this.params.literatureData.orderDate.split('/')
      : [];

    const OrderDate = new Date(
      `20${newOrderDateFormat[1]}/${newOrderDateFormat[0]}`
    );
    const PublicationDate = new Date(
      `${newPublicationDateFormat[0]}/${newPublicationDateFormat[1]}`
    );
    if (OrderDate.getTime() < PublicationDate.getTime()) {
      this.showUpdateNotification = true;
      this.updateNotificationMessage =
        this.translateService.instant('literature.fundname-subtext-updated') +
        ` ${this.params.literatureData.orderDate} ` +
        this.translateService.instant(
          'literature.fundname-subtext-updated-message'
        );
    }
  }

  ngOnDestroy(): void {
    this.loginSubscription?.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
